import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { getWebViewLedgerReportDataApiHandler } from "../../../../services/AdminIndex";
import { parseData, reorderAccountData } from "../../../../utils/ledgerReportCalc";

// Headless-render-only page for the Auto Report Mail worker: Puppeteer
// navigates here, waits for #report-print-ready, then calls page.pdf().
// Not linked from anywhere in the app UI — this is not the interactive
// Ledger Report screen (see finishEntry/reports/LedgerReport.js), but it
// reuses that page's exact JSON-parsing/ordering logic
// (utils/ledgerReportCalc.js) instead of rendering the SP's raw JSON
// columns, which is what this page used to do.
const cellStyle = { border: "1px solid #ddd", padding: "6px 8px" };
const thStyle = { border: "1px solid #ccc", padding: "6px 8px", background: "#f4a261", textAlign: "left" };

function LedgerBlock({ account, fromDate }) {
  const accountData = account.accountData || [];
  const hasOpeningRow = accountData.some((item) => item?.accountName === "*Opening Balance");

  const totalDebit = accountData
    .reduce((total, t) => (t.type === "DR" ? total + Number(t?.amount || 0) : total), 0)
    .toFixed(2);
  const totalCredit = accountData
    .reduce((total, t) => (t.type === "CR" && !t?.isOpening ? total + Number(t?.amount || 0) : total), 0)
    .toFixed(2);
  const closingBalance = Number(accountData[accountData.length - 1]?.balance || 0).toFixed(2);

  const addr = account.address?.[0];
  const addressLine1 = addr
    ? [addr.address1, addr.address2, addr.address3].filter(Boolean).join(", ")
    : "";
  const addressLine2 = addr
    ? [addr.areaName, addr.cityName, addr.Statename && `${addr.Statename}-`, addr.pin].filter(Boolean).join(", ")
    : "";

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 6 }}>
        <b>Ledger Of</b> {account.accountName} &nbsp;&nbsp; <b>Group Name</b> {account.GroupName}
      </div>
      {addressLine1 ? <div>Address: {addressLine1}</div> : null}
      {addressLine2 ? <div>{addressLine2}</div> : null}
      {account.PANNo ? <div>PAN No.: {account.PANNo} &nbsp;&nbsp; TAN No.: {account.TANNo || ""}</div> : null}
      {account.GST ? <div>GST No.: {account.GST}</div> : null}

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginTop: 8 }}>
        <thead>
          <tr>
            {["Doc", "Date", "Cheque No.", "Particulars", "Debit", "Credit", "Balance"].map((h) => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!hasOpeningRow && (
            <tr>
              <td style={cellStyle}></td>
              <td style={cellStyle}>{moment(fromDate).format("DD-MM-YYYY")}</td>
              <td style={cellStyle}></td>
              <td style={cellStyle}>*Opening Balance</td>
              <td style={cellStyle}></td>
              <td style={cellStyle}></td>
              <td style={cellStyle}>0.00</td>
            </tr>
          )}
          {accountData.map((acEntry, idx) => (
            <React.Fragment key={idx}>
              <tr>
                <td style={cellStyle}>{acEntry?.transNo}</td>
                <td style={cellStyle}>{moment(acEntry?.createdAt).format("DD-MM-YYYY")}</td>
                <td style={cellStyle}>{acEntry?.cheque || ""}</td>
                <td style={cellStyle}>{acEntry?.accountName}</td>
                <td style={cellStyle}>{!acEntry?.isOpening && acEntry?.type === "DR" ? Number(acEntry?.amount).toFixed(2) : ""}</td>
                <td style={cellStyle}>{!acEntry?.isOpening && acEntry?.type === "CR" ? Number(acEntry?.amount).toFixed(2) : ""}</td>
                <td style={cellStyle}>{Number(acEntry?.balance).toFixed(2)}</td>
              </tr>
              {acEntry?.narration1 ? (
                <tr>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle}></td>
                  <td style={cellStyle} colSpan={4}>{acEntry.narration1}</td>
                </tr>
              ) : null}
            </React.Fragment>
          ))}
          <tr style={{ background: "#ffe9a8", fontWeight: "bold" }}>
            <td style={cellStyle} colSpan={3}></td>
            <td style={cellStyle}>Total</td>
            <td style={cellStyle}>{totalDebit}</td>
            <td style={cellStyle}>{totalCredit}</td>
            <td style={cellStyle}>{closingBalance}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function PrintLedgerReport() {
  const { companyId, accountId, fromDate, toDate } = useParams();
  const [accounts, setAccounts] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getWebViewLedgerReportDataApiHandler({
      companyId,
      type: "detailReport",
      fromDate,
      toDate,
      accounts: JSON.stringify([Number(accountId)]),
      groups: JSON.stringify([]),
    })
      .then((res) => {
        const filtered = (res?.data || []).filter((ele) => ele?.accountData);
        const formatted = filtered.map((ele) => ({
          ...ele,
          address: parseData(ele?.address),
          accountData: reorderAccountData(parseData(ele?.accountData)),
        }));
        setAccounts(formatted);
      })
      .finally(() => setReady(true));
  }, [companyId, accountId, fromDate, toDate]);

  return (
    <div style={{ fontFamily: "Arial, sans-serif", padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Ledger Report</h2>
      <div style={{ marginBottom: 16, color: "#555" }}>
        {fromDate} to {toDate}
      </div>
      {accounts.length ? (
        accounts.map((account, idx) => (
          <LedgerBlock key={idx} account={account} fromDate={fromDate} />
        ))
      ) : (
        <div>No ledger transactions found for this period.</div>
      )}
      {ready ? <div id="report-print-ready" /> : null}
    </div>
  );
}
